import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { ArrowLeft, ScanBarcode, ShoppingCart, TrendingUp, CreditCard } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';
import { LogoBrain, FlowFeedLogoWhite } from './Logo';
import brandingConfig, { getBrandName, getTagline } from '../config/branding';

// Pre-compute floating icons for POS theme
const FLOATING_ICONS_DATA = (() => {
  const icons = ['barcode', 'cart', 'trend', 'card', 'pos', 'scan'];
  return [...Array(12)].map((_, i) => ({
    id: i,
    initialY: 40 + (((i * 17) % 100) / 100) * 50,
    animateY: 10 + (((i * 23) % 100) / 100) * 30,
    duration: 10 + (((i * 31) % 100) / 100) * 8,
    delay: (((i * 41) % 100) / 100) * 5,
    icon: icons[i % 6],
    left: (i * 9) % 90 + 5
  }));
})();

const AuthLayout = ({ children, title, subtitle, type = "login" }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const renderIcon = (iconType, className) => {
    switch(iconType) {
      case 'barcode': return <ScanBarcode className={className} />;
      case 'cart': return <ShoppingCart className={className} />;
      case 'trend': return <TrendingUp className={className} />;
      case 'card': return <CreditCard className={className} />;
      default: return <ScanBarcode className={className} />;
    }
  };

  return (
    <div className={`min-h-screen flex bg-background ${isRTL ? 'flex-row-reverse' : ''}`}>
      {/* Left Panel - Visual with Emerald/Teal theme */}
      <div className={`hidden lg:flex w-1/2 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 relative overflow-hidden flex-col justify-between p-12 ${isRTL ? 'border-l' : 'border-r'} border-emerald-500/30`}>
        
        {/* Emerald Glow Background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(16,185,129,0.15),_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,_rgba(20,184,166,0.1),_transparent_50%)]"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 pointer-events-none">
            {[...Array(6)].map((_, i) => (
                <motion.div
                    key={`grid-h-${i}`}
                    className="absolute left-0 h-[1px] w-full bg-emerald-500/20"
                    style={{ top: `${i * 20}%` }}
                    animate={{ 
                        opacity: [0.1, 0.3, 0.1],
                    }}
                    transition={{ duration: 4, delay: i * 0.3, repeat: Infinity }}
                />
            ))}
            {[...Array(6)].map((_, i) => (
                <div
                    key={`grid-v-${i}`}
                    className="absolute top-0 w-[1px] h-full bg-emerald-500/10"
                    style={{ left: `${i * 20}%` }}
                />
            ))}
        </div>

        {/* Scanning Line Animation */}
        <div className="absolute top-1/3 left-10 right-10 space-y-2 opacity-60 pointer-events-none">
            <motion.div 
                initial={{ width: "0%", x: 0 }} 
                animate={{ width: ["0%", "100%", "0%"], x: ["0%", "0%", "100%"] }} 
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="h-[2px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
            />
            <div className="flex justify-between font-mono text-[10px] text-emerald-400/70">
                <motion.span animate={{ opacity: [0,1,0] }} transition={{ duration: 2, repeat: Infinity }}>
                  {isRTL ? 'جاري المسح...' : 'SCANNING_INVENTORY...'}
                </motion.span>
                <motion.span animate={{ opacity: [0,1,0] }} transition={{ duration: 2, delay: 0.7, repeat: Infinity }}>
                  {isRTL ? 'تحميل نظام POS...' : 'LOADING_POS_SYSTEM...'}
                </motion.span>
            </div>
        </div>

        {/* Floating POS Icons */}
        {FLOATING_ICONS_DATA.map((item) => (
            <motion.div
                key={`icon-${item.id}`}
                className="absolute pointer-events-none select-none"
                style={{ 
                    left: `${item.left}%`,
                }}
                initial={{ y: `${item.initialY}vh`, opacity: 0 }}
                animate={{ y: `${item.animateY}vh`, opacity: [0, 0.4, 0.4, 0] }} 
                transition={{ 
                    duration: item.duration, 
                    repeat: Infinity, 
                    ease: "linear",
                    delay: item.delay
                }}
            >
                {renderIcon(item.icon, "h-6 w-6 text-emerald-500/40")}
            </motion.div>
        ))}

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/" className={`flex items-center gap-2 text-white mb-8 group w-fit ${isRTL ? 'flex-row-reverse' : ''}`}>
            <FlowFeedLogoWhite size="large" showTagline={true} />
          </Link>
        </div>

        {/* Testimonial Section */}
        <div className={`relative z-10 max-w-md space-y-6 ${isRTL ? 'text-right' : ''}`}>
          <div>
            <blockquote className="text-xl font-medium text-slate-200 leading-relaxed mb-4">
              {isRTL 
                ? `"${getBrandName(false)} غيّر عمليات متجرنا. نظام نقاط البيع سريع وذكي، والتقارير المدعومة بالذكاء الاصطناعي توفر لنا ساعات من العمل يومياً."`
                : `"${getBrandName(false)} transformed our store operations. The POS is lightning fast, and the AI-powered insights save us hours every day."`
              }
            </blockquote>
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80" className="h-12 w-12 rounded-full border-2 border-emerald-500" alt="User" />
              <div>
                <p className="font-bold text-white">{isRTL ? 'مايك طومسون' : 'Mike Thompson'}</p>
                <p className="text-slate-400 text-sm">{isRTL ? 'مالك متجر، أوستن' : 'Store Owner, Austin TX'}</p>
              </div>
            </div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className={`bg-emerald-950/40 border border-emerald-500/20 p-4 rounded-xl backdrop-blur-sm relative ${isRTL ? 'mr-8' : 'ml-8'}`}
          >
             <div className={`absolute ${isRTL ? '-right-4' : '-left-4'} top-0 h-full w-4 ${isRTL ? 'border-r-2 border-b-2 rounded-br-xl' : 'border-l-2 border-b-2 rounded-bl-xl'} border-emerald-500/20 opacity-50 h-[50%] top-[-50%]`}></div>
             
             <p className="text-sm text-emerald-100 leading-relaxed mb-3">
               {isRTL 
                 ? `"شكراً مايك! مهمتنا هي جعل كل عملية بيع أسرع وأذكى. ${getBrandName(false)} مصمم للنمو مع متجرك!"`
                 : `"Thanks Mike! Our mission is to make every sale faster and smarter. ${getBrandName(false)} is built to grow with your store!"`
               }
             </p>
             <div className={`flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-xs font-bold text-white shadow-lg shadow-emerald-500/20 ring-2 ring-emerald-500/20">
                  AA
                </div>
                <div className={isRTL ? 'text-right' : ''}>
                   <p className="text-xs font-bold text-white">{isRTL ? 'أياز علي' : 'Ayaz Ali'}</p>
                   <p className="text-[10px] text-emerald-300 uppercase tracking-wide">{isRTL ? `مؤسس ${getBrandName(false)}` : `Founder, ${brandingConfig.companyName}`}</p>
                </div>
             </div>
          </motion.div>

          {/* Stats Bar */}
          <div className="flex items-center gap-6 pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">500+</p>
              <p className="text-xs text-slate-400">{isRTL ? 'متاجر' : 'Stores'}</p>
            </div>
            <div className="h-8 w-px bg-emerald-500/20"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">2M+</p>
              <p className="text-xs text-slate-400">{isRTL ? 'معاملات' : 'Transactions'}</p>
            </div>
            <div className="h-8 w-px bg-emerald-500/20"></div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">99.9%</p>
              <p className="text-xs text-slate-400">{isRTL ? 'وقت التشغيل' : 'Uptime'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative bg-background">
        <div className={`absolute top-8 ${isRTL ? 'right-8' : 'left-8'} flex items-center gap-4`}>
          <Link to="/" className={`flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-emerald-600 transition-colors group ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="p-2 rounded-full bg-muted group-hover:bg-emerald-500/10 transition-colors">
               <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
            </div>
            {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
          </Link>
        </div>
        
        <div className={`absolute top-8 ${isRTL ? 'left-8' : 'right-8'} flex items-center gap-2`}>
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
        
        <div className="w-full max-w-sm space-y-8">
          <div className={`${isRTL ? 'text-right' : 'text-center lg:text-left'}`}>
            <h1 className="text-3xl font-bold font-heading mb-2">{title}</h1>
            <p className="text-muted-foreground">{subtitle}</p>
          </div>

          {children}

          <p className="text-center text-sm text-muted-foreground mt-6">
            {type === 'login' ? (
              <>{isRTL ? 'ليس لديك حساب؟' : "Don't have an account?"} <Link to="/signup" className="text-emerald-600 font-medium hover:underline">{isRTL ? 'سجل الآن' : 'Sign up'}</Link></>
            ) : (
              <>{isRTL ? 'لديك حساب بالفعل؟' : 'Already have an account?'} <Link to="/login" className="text-emerald-600 font-medium hover:underline">{isRTL ? 'تسجيل الدخول' : 'Log in'}</Link></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
